/**
 * Convert a prototype / design internal-link href into a real Nuxt route.
 *
 * This Nuxt app IS sharkfoto.com, so on-site links resolve to relative paths.
 *
 * Examples:
 *   "https://sharkfoto.com/free-tools/"        → "/free-tools"
 *   "https://sharkfoto.com/"                    → "/"
 *   "https://sharkfoto.com/ai/image/x"         → "/ai/image/x"
 *   "https://example.com/foo"                  → unchanged (other host)
 *   "#effects"                                  → "#effects"
 *   "Image Converter.html?tool=png-to-dst"     → "/free-tools/image/converter/png-to-dst"
 *   "Image Converter.html"                      → "/free-tools/image/converter"
 *   "../SharkFoto Homepage.html"                → "/"
 *   "Free Tools.html"                           → "/free-tools"
 *   "AVIF Background Remover.html"              → "/free-tools/avif-background-remover"
 *
 * Rules:
 *  - absolute http(s) on sharkfoto.com → strip host, return the path
 *  - other absolute hosts → unchanged
 *  - starts with "#" → unchanged
 *  - else derive slug (from ?tool=<slug> else slugified filename) and map to a route
 */

import { getToolSEO } from "@/settings/free-tools/seo-data";

// Single-tool slugs → 描述性规范路由(claude design P4①②确认)。
// resize/crop/flip/rotate 走新设计 catch-all(FreeToolPage 分发到 ResizeTool/CropTool/FlipTool/RotateTool),
// 旧 UCard 页 resize.vue/flipper.vue/rotator.vue 退役。converter/compressor 暂留(P4①B:改接新 ToolStagePlaceholder+后端)。
const SINGLE_TOOL_ROUTES = {
  "image-converter": "/free-tools/image/converter",
  "image-compressor": "/free-tools/image/compressor",
  "resize-image": "/free-tools/image/resize-image",
  "crop-image": "/free-tools/image/crop-image",
  "flip-image": "/free-tools/image/flip-image",
  "rotate-image": "/free-tools/image/rotate-image",
};

// Trim a trailing slash, but never reduce "/" to "".
function trimTrailingSlash(p) {
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

export function ftHref(raw) {
  if (!raw) return "/free-tools";
  const str = String(raw).trim();

  // anchor links pass through untouched
  if (str.startsWith("#")) return str;

  // absolute http(s): same-origin sharkfoto.com → strip host to a path; others unchanged
  if (/^https?:\/\//i.test(str)) {
    try {
      const u = new URL(str);
      if (/(^|\.)sharkfoto\.com$/i.test(u.hostname)) {
        const path = (u.pathname || "/") + (u.search || "") + (u.hash || "");
        return trimTrailingSlash(path);
      }
    } catch {
      /* malformed URL — fall through and return as-is below */
    }
    return str;
  }

  // prototype link → derive a slug (prefer ?tool=<slug>, else slugify the filename)
  const toolMatch = str.match(/[?&]tool=([^&]+)/);
  let slug;
  if (toolMatch && toolMatch[1]) {
    slug = decodeURIComponent(toolMatch[1]).toLowerCase();
  } else {
    const fileName = str.split("?")[0].split("#")[0].replace(/\.html?$/i, "");
    slug = fileName
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }

  // named destinations
  if (slug === "sharkfoto-homepage") return "/";
  if (slug === "free-tools") return "/free-tools";

  // 形如 a-to-b 但其实是图片工具(canonical 已在 /free-tools/image/<slug>,非 /converter/):ascii/base64/grayscale。
  // 必须在下面 a-to-b 配对判断之前拦截,否则会被当成转换器配对(#19 ④,claude design 确认 canonical 已对)。
  if (["image-to-ascii", "image-to-base64", "image-to-grayscale"].includes(slug)) {
    return "/free-tools/image/" + slug;
  }

  // converter pairs "a-to-b" → 规范路径由 getToolSEO 单一来源决定(video/audio/image,
  // 以及不可写目标的 explainer /image/<slug>)。未知 slug 回退旧行为。修视频/音频配对被错放到 /image/(nuxt#44)。
  if (/^[a-z0-9]+-to-[a-z0-9]+$/.test(slug)) {
    try {
      const seo = getToolSEO(slug);
      const u = seo && (seo.canonical || seo.url);
      if (u) return trimTrailingSlash(u.replace(/^https?:\/\/[^/]+/i, ""));
    } catch {}
    return "/free-tools/image/converter/" + slug; // fallback if unknown
  }

  // known single tools
  if (SINGLE_TOOL_ROUTES[slug]) return SINGLE_TOOL_ROUTES[slug];

  // audio-fade 修复:工具名 "Audio Fade In/Out" 的 canonical slug 是 audio-fade-in-out,但 href "Audio Fade.html"
  // → audio-fade,二者不一致导致 /free-tools/audio-fade 404。直指 canonical(#19,已上报 claude design 确认最终 slug)。
  if (slug === "audio-fade") return "/free-tools/audio/audio-fade";

  // fallback
  return trimTrailingSlash("/free-tools/" + slug);
}
